import type { WidgetProps } from '@rjsf/utils'
import { Mtds, PlusIcon } from 'prove-common'
import { useEffect, useMemo, useState } from 'react'
import { CreateSubstanceModal } from '../../create-substance-modal/CreateSubstanceModal'

export const SelectSubstancesWidget = ({
  disabled,
  id,
  onChange,
  options: { enumOptions: options },
  placeholder,
  required,
  value
}: WidgetProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  // When a substance is created, store it here and wait until options update.
  const [pendingCreated, setPendingCreated] = useState<{
    id: number
    code?: string
  } | null>(null)
  const selectedValues = new Set(value.map(String))
  const selected = options
    ?.filter((o) => selectedValues.has(String(o.value)))
    .map(({ label, value }) => ({ label, value: `${value}` })) // Convert value to string for HTML compatibility

  const isAnySelectionNewCombination = options?.some(({ schema }) => {
    return selected?.some(
      (sel) =>
        sel.value === `${schema?.const}` &&
        schema?.['x-new-combination'] === true
    )
  })

  const { regularOptions, newCombinationOptions } = useMemo(() => {
    const regular = (options ?? []).filter(
      ({ schema }) => schema?.['x-new-combination'] !== true
    )
    const newCombos = (options ?? []).filter(
      ({ schema }) => schema?.['x-new-combination'] === true
    )
    return { regularOptions: regular, newCombinationOptions: newCombos }
  }, [options])

  const handleSelectedChange = (
    items: Array<{ label: string; value: string }>
  ) => {
    const itemValues = new Set(items.map((item) => item.value))
    const optValues = options?.map((o) => o.value)
    onChange(optValues?.filter((v) => itemValues.has(`${v}`))) // Get original value format
  }

  const handleCreatedSelect = (created: { id: number; code?: string }) => {
    // Defer selection until options are updated by query cache refetch.
    setPendingCreated(created)
  }

  const openCreateDialog = () => {
    ;(document.activeElement as HTMLElement | null)?.blur()
    setIsDialogOpen(true)
  }

  // Once options include the newly created item, select it and clear pending state.
  useEffect(() => {
    if (!pendingCreated) return
    const match = options?.find((o) => o.value === pendingCreated.id)
    if (!match) return

    const nextSelected = [
      ...(selected ?? []),
      { label: match.label, value: `${match.value}` }
    ]
    handleSelectedChange(nextSelected)
    setIsDialogOpen(false)
    setPendingCreated(null)
  }, [options, pendingCreated])

  return (
    <>
      <Mtds.Field.Suggestion
        data-color={isAnySelectionNewCombination ? 'info' : undefined}
        data-multiple
        selected={selected}
        onSelectedChange={handleSelectedChange}
      >
        <Mtds.Input
          disabled={disabled}
          id={id}
          placeholder={placeholder}
          required={required}
        />
        <button type='reset' aria-label='Fjern tekst' />
        <Mtds.Field.Datalist>
          {regularOptions.map(({ label, value }) => (
            <Mtds.Field.Option key={value} value={value}>
              {label}
            </Mtds.Field.Option>
          ))}

          {regularOptions.length > 0 && newCombinationOptions.length > 0 && (
            <div role='separator'>
              <Mtds.Divider data-gap={0} />
            </div>
          )}

          {newCombinationOptions.map(({ label, value }) => {
            return (
              <Mtds.Field.Option key={value} value={value} data-color={'info'}>
                <Mtds.Flex data-justify='space-between'>
                  {label}
                  <Mtds.Tag data-color={'info'} data-size='sm'>
                    Ny kombinasjon
                  </Mtds.Tag>
                </Mtds.Flex>
              </Mtds.Field.Option>
            )
          })}

          <div role='separator'>
            <Mtds.Divider data-gap={0} />
          </div>

          <Mtds.Button
            className='w-full'
            data-variant='tertiary'
            onMouseDown={(event) => event.preventDefault()}
            onClick={openCreateDialog}
          >
            <PlusIcon />
            Opprett ny
          </Mtds.Button>
        </Mtds.Field.Datalist>
      </Mtds.Field.Suggestion>

      {/* Modal for creating a new entry */}
      {isDialogOpen && (
        <CreateSubstanceModal
          isOpen={isDialogOpen}
          setOpen={setIsDialogOpen}
          onCreated={handleCreatedSelect}
        />
      )}
    </>
  )
}
